// ─── Launch Tier Provisioning Wizard (AC-13, AC-14, AC-17) ──────
// Full-screen wizard: project name → review → provision with progress.
// Calls the orchestrator API which provisions all infra in sequence.

import React, { useState, useEffect, useRef } from 'react';
import {
  Server, Database, Globe, Mail, GitBranch, Check, Loader2,
  AlertTriangle, ExternalLink, ChevronRight, ArrowLeft,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authHeaders } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

// ─── AC-17: Resource name mapping ───────────────────────────────

function toResourceName(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

// ─── Types ──────────────────────────────────────────────────────

type WizardStep = 'name' | 'review' | 'provisioning' | 'complete';

interface ProviderStatus {
  status: 'pending' | 'running' | 'complete' | 'failed';
  detail?: string;
  error?: string;
}

// ─── Styles ─────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--surface2)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: 16,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 16px', fontSize: 'var(--text-base)',
  fontFamily: 'var(--font-mono)', background: 'var(--surface3)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
  color: 'var(--text)', outline: 'none', boxSizing: 'border-box' as const,
};

const btnPrimary: React.CSSProperties = {
  padding: '12px 24px', fontSize: 'var(--text-sm)', fontWeight: 600,
  background: 'var(--orange)', border: 'none', borderRadius: 'var(--radius-md)',
  color: 'white', cursor: 'pointer', fontFamily: 'var(--font-sans)',
  display: 'inline-flex', alignItems: 'center', gap: 8,
};

// ─── Provider Status Row ────────────────────────────────────────

const PROVIDER_META: Record<string, { icon: React.ElementType; label: string }> = {
  github: { icon: GitBranch, label: 'GitHub Repository' },
  supabase: { icon: Database, label: 'Supabase Database' },
  railway: { icon: Server, label: 'Railway API' },
  netlify: { icon: Globe, label: 'Netlify Frontend' },
  env_inject: { icon: Server, label: 'Environment Variables' },
};

function ProviderRow({ id, state }: { id: string; state: ProviderStatus }) {
  const meta = PROVIDER_META[id] || { icon: Server, label: id };
  const Icon = meta.icon;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      background: 'var(--surface3)', borderRadius: 'var(--radius-md)', marginBottom: 8,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 'var(--radius-md)',
        background: state.status === 'complete' ? 'var(--green-dim, rgba(34,197,94,0.1))'
          : state.status === 'failed' ? 'var(--red-dim, rgba(239,68,68,0.1))'
          : state.status === 'running' ? 'var(--orange-dim, rgba(0,229,255,0.1))'
          : 'var(--surface2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {state.status === 'complete' ? <Check size={16} style={{ color: 'var(--green)' }} />
          : state.status === 'failed' ? <AlertTriangle size={16} style={{ color: 'var(--red)' }} />
          : state.status === 'running' ? <Loader2 size={16} style={{ color: 'var(--orange)', animation: 'spin 1s linear infinite' }} />
          : <Icon size={16} style={{ color: 'var(--text-muted)' }} />}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text)' }}>{meta.label}</div>
        {state.detail && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{state.detail}</div>}
        {state.error && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--red)' }}>{state.error}</div>}
      </div>
      <div style={{ fontSize: 'var(--text-xs)', color: state.status === 'complete' ? 'var(--green)' : state.status === 'failed' ? 'var(--red)' : 'var(--text-muted)' }}>
        {state.status === 'complete' ? 'Ready' : state.status === 'failed' ? 'Failed' : state.status === 'running' ? 'Provisioning...' : 'Pending'}
      </div>
    </div>
  );
}

// ─── Main Wizard ────────────────────────────────────────────────

export function LaunchWizard({ onComplete, onBack }: { onComplete: () => void; onBack: () => void }) {
  const { subscriber } = useAuth();
  const [step, setStep] = useState<WizardStep>('name');
  const [projectName, setProjectName] = useState('');
  const [region] = useState('us-east-1');
  const [providers, setProviders] = useState<Record<string, ProviderStatus>>({
    github: { status: 'pending' },
    supabase: { status: 'pending' },
    railway: { status: 'pending' },
    netlify: { status: 'pending' },
    env_inject: { status: 'pending' },
  });
  const [finalResult, setFinalResult] = useState<{ apiUrl?: string; siteUrl?: string; repoUrl?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const provisioningRef = useRef(false);

  const resourceName = toResourceName(projectName);
  const nameValid = resourceName.length >= 3 && resourceName.length <= 40;

  // ── Start provisioning via API ──────────────────────────────
  async function startProvisioning() {
    if (provisioningRef.current) return;
    provisioningRef.current = true;
    setStep('provisioning');
    setError(null);

    try {
      const auth = await authHeaders();
      const res = await fetch(`${API_BASE}/api/launch/provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({
          project_name: projectName,
          resource_name: resourceName,
          subscriber_id: subscriber?.id,
          region,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        setError(data.error || `Provisioning failed: HTTP ${res.status}`);
        provisioningRef.current = false;
        return;
      }

      // Stream SSE events for progress
      const sseUrl = `${API_BASE}/api/launch/provision/${resourceName}/status`;
      const eventSource = new EventSource(sseUrl);

      eventSource.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'step') {
            setProviders((prev: Record<string, ProviderStatus>) => ({
              ...prev,
              [data.step]: {
                status: data.status,
                detail: data.detail || data.url,
                error: data.error,
              },
            }));
          }

          if (data.type === 'complete') {
            setFinalResult({
              apiUrl: data.api_url,
              siteUrl: data.site_url,
              repoUrl: data.repo_url,
            });
            setStep('complete');
            eventSource.close();
          }

          if (data.type === 'error') {
            setProviders((prev: Record<string, ProviderStatus>) => ({
              ...prev,
              [data.step]: { status: 'failed', error: data.message },
            }));
          }
        } catch {
          // ignore parse errors
        }
      };

      eventSource.onerror = () => {
        // SSE disconnected — check final state via polling fallback
        eventSource.close();
        // If we're still on provisioning step, poll once for result
        if (step === 'provisioning') {
          setTimeout(async () => {
            try {
              const statusAuth = await authHeaders();
              const statusRes = await fetch(`${API_BASE}/api/launch/provision/${resourceName}/result`, { headers: statusAuth });
              if (statusRes.ok) {
                const result = await statusRes.json();
                if (result.success) {
                  setStep('complete');
                  setFinalResult(result);
                }
              }
            } catch { /* ignore */ }
          }, 3000);
        }
      };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Provisioning failed');
      provisioningRef.current = false;
    }
  }

  // ── Step 1: Project name ────────────────────────────────────
  if (step === 'name') {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-sm)' }}>
          <ArrowLeft size={14} /> Back
        </button>

        <div style={cardStyle}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, marginBottom: 4 }}>Launch your SaaS</h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 24 }}>
            OCNexus will provision all infrastructure for you. Just name your project.
          </p>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, display: 'block' }}>
              Project name
            </label>
            <input type="text" value={projectName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectName(e.target.value)}
              placeholder="My SaaS App"
              style={inputStyle} />
            {projectName && (
              <div style={{ fontSize: 'var(--text-xs)', color: nameValid ? 'var(--text-muted)' : 'var(--red)', marginTop: 6, fontFamily: 'var(--font-mono)' }}>
                Resource name: <strong>{resourceName || '...'}</strong>
                {!nameValid && ' (must be 3-40 characters)'}
              </div>
            )}
          </div>

          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.5, padding: '12px 14px', background: 'var(--surface3)', borderRadius: 'var(--radius-md)' }}>
            <strong style={{ color: 'var(--text)' }}>We'll provision:</strong>
            <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              <span>GitHub repo</span><span>Supabase database</span>
              <span>Railway API</span><span>Netlify frontend</span>
            </div>
          </div>

          <button onClick={() => setStep('review')} disabled={!nameValid}
            style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: nameValid ? 1 : 0.5 }}>
            Review setup <ChevronRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  // ── Step 2: Review ──────────────────────────────────────────
  if (step === 'review') {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <button onClick={() => setStep('name')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--text-sm)' }}>
          <ArrowLeft size={14} /> Back
        </button>

        <div style={cardStyle}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, marginBottom: 16 }}>Review your setup</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Project name</span>
              <span style={{ color: 'var(--text)', fontWeight: 500 }}>{projectName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Resource name</span>
              <span style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{resourceName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Region</span>
              <span style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{region}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)', padding: '8px 0' }}>
              <span style={{ color: 'var(--text-muted)' }}>Estimated time</span>
              <span style={{ color: 'var(--text)' }}>3-5 minutes</span>
            </div>
          </div>

          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 20, padding: '10px 14px', background: 'var(--surface3)', borderRadius: 'var(--radius-md)', lineHeight: 1.5 }}>
            This will create a GitHub repo, Supabase database, Railway API service, and Netlify frontend — all linked together.
          </div>

          <button onClick={startProvisioning}
            style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }}>
            Start provisioning <ChevronRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  // ── Step 3: Provisioning progress (AC-14) ───────────────────
  if (step === 'provisioning') {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={cardStyle}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, marginBottom: 4 }}>Setting up your SaaS...</h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 24 }}>This takes about 3-5 minutes.</p>

          {Object.entries(providers).map(([id, state]: [string, ProviderStatus]) => (
            <ProviderRow key={id} id={id} state={state} />
          ))}

          {error && (
            <div style={{ padding: '12px 16px', background: 'var(--red-dim)', border: '1px solid var(--red)', borderRadius: 'var(--radius-md)', marginTop: 16, fontSize: 'var(--text-xs)', color: 'var(--red)' }}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Step 4: Complete ────────────────────────────────────────
  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%', margin: '0 auto 16px',
            background: 'var(--green-dim, rgba(34,197,94,0.1))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Check size={24} style={{ color: 'var(--green)' }} />
          </div>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, marginBottom: 4 }}>Your SaaS is live</h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{projectName} is ready to go.</p>
        </div>

        {finalResult && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            {finalResult.apiUrl && (
              <a href={finalResult.apiUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--surface3)', borderRadius: 'var(--radius-md)', textDecoration: 'none', color: 'var(--text)', fontSize: 'var(--text-sm)' }}>
                <span><strong>API</strong> <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{finalResult.apiUrl}</span></span>
                <ExternalLink size={12} style={{ color: 'var(--text-muted)' }} />
              </a>
            )}
            {finalResult.siteUrl && (
              <a href={finalResult.siteUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--surface3)', borderRadius: 'var(--radius-md)', textDecoration: 'none', color: 'var(--text)', fontSize: 'var(--text-sm)' }}>
                <span><strong>Dashboard</strong> <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{finalResult.siteUrl}</span></span>
                <ExternalLink size={12} style={{ color: 'var(--text-muted)' }} />
              </a>
            )}
            {finalResult.repoUrl && (
              <a href={finalResult.repoUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--surface3)', borderRadius: 'var(--radius-md)', textDecoration: 'none', color: 'var(--text)', fontSize: 'var(--text-sm)' }}>
                <span><strong>Repo</strong> <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{finalResult.repoUrl}</span></span>
                <ExternalLink size={12} style={{ color: 'var(--text-muted)' }} />
              </a>
            )}
          </div>
        )}

        <button onClick={onComplete}
          style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }}>
          Open Dashboard <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
